import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  DocumentReference,
  Timestamp,
} from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db } from "@/firebase";
import { formatPhoneNumber } from "@/utils/phone";

interface Mail {
  id: string;
  customerEmail: string;
  subject: string;
}

interface MailMessageDetail {
  id: string;
  from: string;
  to: string;
  subject?: string;
  body?: string;
  bodyPreview?: string;
}

interface MailTemplate {
  id: string;
  title: string;
  subject: string;
  body: string;
  attach?: string;
  sign?: string;
  sortNo?: number;
}

interface TemplateContext {
  order: Record<string, any>;
  user: Record<string, any>;
  shikaku: Record<string, any>;
  samplesend: Record<string, any>;
}

const ensureReplySubject = (subject: string) => {
  const trimmed = subject.trim();
  if (!trimmed) return "Re:";
  return trimmed.toLowerCase().startsWith("re:") ? trimmed : `Re: ${trimmed}`;
};

const normalizeValue = (value: any, key?: string, seen: WeakSet<object> = new WeakSet()): any => {
  if (value === null || value === undefined) return value;

  if (value instanceof Timestamp) {
    return value.toDate().toLocaleString("ja-JP");
  }

  if (value instanceof DocumentReference) {
    return value.path || value.id;
  }

  if (typeof value === "string" && key?.toLowerCase().includes("phone")) {
    return formatPhoneNumber(value);
  }

  if (typeof value === "object") {
    if (seen.has(value)) {
      return "[Circular]";
    }
    seen.add(value);

    if (Array.isArray(value)) {
      return value.map((item) => normalizeValue(item, undefined, seen));
    }

    return Object.fromEntries(
      Object.entries(value).map(([childKey, val]) => [childKey, normalizeValue(val, childKey, seen)])
    );
  }

  return value;
};

const getTemplateValue = (context: TemplateContext, keyPath: string) => {
  const pathParts = keyPath.split(".");
  if (pathParts.length < 2) return undefined;
  const [rootKey, ...rest] = pathParts;
  let current: any = context[rootKey as keyof TemplateContext];
  for (const part of rest) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== "object") return undefined;
    current = Object.prototype.hasOwnProperty.call(current, part) ? current[part] : undefined;
  }
  return current;
};

const formatTemplateValue = (value: unknown) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") {
    return Number.isNaN(value) ? "" : String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => formatTemplateValue(item))
      .filter((item) => item !== "")
      .join(", ");
  }
  return String(normalizeValue(value));
};

const applyTemplateVariables = (templateText: string, context: TemplateContext) =>
  templateText.replace(/{{\s*([\w.]+)\s*}}/g, (_match, key) => {
    const rawValue = getTemplateValue(context, String(key));
    return formatTemplateValue(rawValue);
  });

export default function MailComposePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const replyMailId = searchParams.get("mailId") || "";
  const replyMessageId = searchParams.get("messageId") || "";
  const orderId = searchParams.get("orderId") || "";
  const sampleSendId = searchParams.get("sampleSendId") || "";
  const [mail, setMail] = useState<Mail | null>(null);
  const [message, setMessage] = useState<MailMessageDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [userOptions, setUserOptions] = useState<{ id: string; name: string }[]>([]);
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [templateContext, setTemplateContext] = useState<TemplateContext | null>(null);
  const [composeForm, setComposeForm] = useState({
    status: "",
    accountId: "",
    from: "",
    to: "",
    subject: "",
    cc: "",
    bcc: "",
    body: "",
    attach: "",
    sign: "",
    templateId: "",
  });

  const isReply = Boolean(replyMailId && replyMessageId);

  const resolveTemplateText = (text: string) => (templateContext ? applyTemplateVariables(text, templateContext) : text);

  useEffect(() => {
    const preloadUsers = async () => {
      try {
        const usersSnapshot = await getDocs(query(collection(db, "User"), where("role", "==", "admin")));
        const users = usersSnapshot.docs.map((userDoc) => {
          const data = userDoc.data() as { display_name?: string; display_Name?: string; email?: string };
          return {
            id: userDoc.id,
            name: data.display_name || data.display_Name || data.email || userDoc.id,
          };
        });
        setUserOptions(users);
      } catch (err) {
        console.error("Failed to load users for mail compose", err);
      }
    };

    preloadUsers();
  }, []);

  useEffect(() => {
    const preloadTemplates = async () => {
      try {
        const templatesQuery = query(collection(db, "mail_templates"), orderBy("sortNo", "asc"));
        const snapshot = await getDocs(templatesQuery);
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() as Omit<MailTemplate, "id">),
        }));
        setTemplates(data);
      } catch (err) {
        console.error("Failed to load mail templates", err);
      }
    };

    preloadTemplates();
  }, []);

  useEffect(() => {
    if (isReply) return;
    let isMounted = true;

    const buildTemplateContext = async (
      orderSource: Record<string, any> | null,
      sampleSendSource: Record<string, any> | null
    ): Promise<TemplateContext> => {
      const orderContext = orderSource ? { ...orderSource } : {};
      const samplesendContext = sampleSendSource ? { ...sampleSendSource } : {};
      let userContext: Record<string, any> = {};
      let shikakuContext: Record<string, any> = {};

      const userRef = orderSource?.UserRef;
      if (userRef) {
        const resolvedRef = userRef.path ? userRef : doc(db, "User", userRef);
        const snapshot = await getDoc(resolvedRef);
        if (snapshot.exists()) {
          userContext = snapshot.data() as Record<string, any>;
        }
      }

      const shikakuRef = orderSource?.shikakuId;
      if (shikakuRef) {
        const resolvedRef = shikakuRef.path ? shikakuRef : doc(db, "Shikaku", shikakuRef);
        const snapshot = await getDoc(resolvedRef);
        if (snapshot.exists()) {
          shikakuContext = snapshot.data() as Record<string, any>;
        }
      }

      return {
        order: orderContext,
        user: userContext,
        shikaku: shikakuContext,
        samplesend: samplesendContext,
      };
    };

    const preloadTemplateContext = async () => {
      setLoading(true);
      try {
        let orderContext: Record<string, any> | null = null;
        let sampleSendContext: Record<string, any> | null = null;

        if (orderId) {
          const orderSnapshot = await getDoc(doc(db, "Order", orderId));
          if (orderSnapshot.exists()) {
            orderContext = {
              orderId: orderSnapshot.id,
              ...(orderSnapshot.data() as Record<string, any>),
            };
          }
        }

        if (sampleSendId) {
          const sampleSnapshot = await getDoc(doc(db, "samplesend", sampleSendId));
          if (sampleSnapshot.exists()) {
            sampleSendContext = {
              sampleSendId: sampleSnapshot.id,
              ...(sampleSnapshot.data() as Record<string, any>),
            };
          }
        }

        const nextContext = await buildTemplateContext(orderContext, sampleSendContext);
        if (!isMounted) return;
        setTemplateContext(nextContext);

        const nextEmail = nextContext.user?.email || nextContext.samplesend?.email || "";
        if (nextEmail) {
          setComposeForm((prev) => ({ ...prev, to: prev.to || nextEmail }));
        }
      } catch (err) {
        console.error("Failed to load template context for mail compose", err);
        setSendError("テンプレート情報の取得に失敗しました。");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    preloadTemplateContext();

    return () => {
      isMounted = false;
    };
  }, [orderId, sampleSendId, isReply]);

  useEffect(() => {
    const currentFrom = auth.currentUser?.email || "";
    if (!replyMailId || !replyMessageId) {
      setComposeForm((prev) => ({
        ...prev,
        from: currentFrom || prev.from,
      }));
      return;
    }

    let isMounted = true;
    const loadReplyData = async () => {
      setLoading(true);
      try {
        const [mailSnapshot, messageSnapshot] = await Promise.all([
          getDoc(doc(db, "mail", replyMailId)),
          getDoc(doc(db, "mail_messages", replyMessageId)),
        ]);
        if (!isMounted) return;

        const mailData = mailSnapshot.exists()
          ? ({ id: mailSnapshot.id, ...(mailSnapshot.data() as Mail) } as Mail)
          : null;
        const messageData = messageSnapshot.exists()
          ? ({ id: messageSnapshot.id, ...(messageSnapshot.data() as MailMessageDetail) } as MailMessageDetail)
          : null;

        setMail(mailData);
        setMessage(messageData);

        setComposeForm((prev) => ({
          ...prev,
          from: currentFrom || prev.from,
          to: messageData?.from || mailData?.customerEmail || prev.to,
          subject: messageData?.subject
            ? ensureReplySubject(messageData.subject)
            : mailData?.subject
            ? ensureReplySubject(mailData.subject)
            : prev.subject,
        }));
      } catch (err) {
        console.error("Failed to load reply data", err);
        setSendError("返信データの取得に失敗しました。");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReplyData();

    return () => {
      isMounted = false;
    };
  }, [replyMailId, replyMessageId]);

  useEffect(() => {
    if (!templateContext || !composeForm.templateId) return;
    const selectedTemplate = templates.find((template) => template.id === composeForm.templateId);
    if (!selectedTemplate) return;
    setComposeForm((prev) => ({
      ...prev,
      subject: selectedTemplate.subject ? resolveTemplateText(selectedTemplate.subject) : prev.subject,
      body: selectedTemplate.body ? resolveTemplateText(selectedTemplate.body) : prev.body,
      attach: selectedTemplate.attach ? resolveTemplateText(selectedTemplate.attach) : prev.attach,
      sign: selectedTemplate.sign ? resolveTemplateText(selectedTemplate.sign) : prev.sign,
    }));
  }, [templateContext, composeForm.templateId, templates]);

  const replyContext = useMemo(() => {
    if (!isReply) return null;
    return {
      from: message?.from || mail?.customerEmail || "-",
      subject: mail?.subject || message?.subject || "-",
      body: message?.body || message?.bodyPreview || "",
    };
  }, [isReply, mail, message]);

  const handleSend = async () => {
    const body = composeForm.body.trim();
    const from = (composeForm.from || auth.currentUser?.email || "").trim();
    const to = composeForm.to.trim();
    const subject = composeForm.subject.trim();
    const cc = composeForm.cc.trim();
    const bcc = composeForm.bcc.trim();
    const attach = composeForm.attach.trim();
    const sign = composeForm.sign.trim();
    const status = composeForm.status.trim();

    if (!body || !to) {
      setSendError("To / 本文 は必須です。");
      return;
    }

    setSending(true);
    setSendError(null);
    try {
      const currentUser = auth.currentUser;
      let mailRef = replyMailId ? doc(db, "mail", replyMailId) : null;

      if (!mailRef) {
        const newMail = {
          customerEmail: to,
          subject: subject || "(no subject)",
          status: status || "waiting",
          spamLevel: 0,
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
        };
        const mailDoc = await addDoc(collection(db, "mail"), newMail);
        mailRef = mailDoc;
      } else {
        await updateDoc(mailRef, {
          lastMessageAt: serverTimestamp(),
          status: status || "waiting",
          subject: subject || mail?.subject || "(no subject)",
        });
      }

      const bodyWithSignature = sign ? `${body}\n\n${sign}` : body;
      await addDoc(collection(db, "mail_messages"), {
        mailId: mailRef,
        direction: "out",
        status,
        from,
        to,
        subject: subject || "(no subject)",
        cc,
        bcc,
        body: bodyWithSignature,
        attach,
        sign,
        staffUserId: currentUser?.uid,
        createdAt: serverTimestamp(),
      });

      navigate(`/admin/mail/${mailRef.id}`);
    } catch (err) {
      console.error("Failed to send mail message", err);
      setSendError("送信に失敗しました。");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline"
              onClick={() => navigate("/admin/mail")}
            >
              ← メール一覧に戻る
            </button>
            <h1 className="mt-2 text-2xl font-bold text-gray-800">
              {isReply ? "返信メールの作成" : "新規メール作成"}
            </h1>
            <p className="text-sm text-gray-500">
              SMTP 送信は mail_messages 追加後に Cloud Functions で実行されます。
            </p>
          </div>
        </div>

        {replyContext && (
          <div className="rounded-lg border bg-white p-4 text-sm text-gray-700 shadow-sm">
            <div className="text-xs font-semibold text-gray-500">返信元のメール</div>
            <div className="mt-2 space-y-1">
              <div>From: {replyContext.from}</div>
              <div>件名: {replyContext.subject}</div>
              {replyContext.body && (
                <div className="mt-2 rounded border border-dashed border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 whitespace-pre-wrap">
                  {replyContext.body}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="compose-template">
              テンプレート
            </label>
            <select
              id="compose-template"
              className="w-full rounded-lg border px-3 py-2"
              value={composeForm.templateId}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selectedTemplate = templates.find((template) => template.id === selectedId);
                setComposeForm((prev) => ({
                  ...prev,
                  templateId: selectedId,
                  subject: selectedTemplate?.subject ? resolveTemplateText(selectedTemplate.subject) : prev.subject,
                  body: selectedTemplate?.body ? resolveTemplateText(selectedTemplate.body) : prev.body,
                  attach: selectedTemplate?.attach ? resolveTemplateText(selectedTemplate.attach) : prev.attach,
                  sign: selectedTemplate?.sign ? resolveTemplateText(selectedTemplate.sign) : prev.sign,
                }));
              }}
              disabled={loading}
            >
              <option value="">テンプレートを選択</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              テンプレートの本文には {"{{"}変数{"}}"} を含められます。送信前に必要に応じて書き換えてください。
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="compose-status">
                ステータス
              </label>
              <select
                id="compose-status"
                className="w-full rounded-lg border px-3 py-2"
                value={composeForm.status}
                onChange={(e) => setComposeForm((prev) => ({ ...prev, status: e.target.value }))}
                disabled={loading}
              >
                <option value="">選択してください</option>
                <option value="open">open</option>
                <option value="waiting">waiting</option>
                <option value="closed">closed</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="compose-account">
                担当者
              </label>
              <select
                id="compose-account"
                className="w-full rounded-lg border px-3 py-2"
                value={composeForm.accountId}
                onChange={(e) => setComposeForm((prev) => ({ ...prev, accountId: e.target.value }))}
                disabled={loading}
              >
                <option value="">担当者を選択</option>
                {userOptions.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="compose-to">
              To
            </label>
            <input
              id="compose-to"
              type="email"
              className="w-full rounded-lg border px-3 py-2"
              value={composeForm.to}
              onChange={(e) => setComposeForm((prev) => ({ ...prev, to: e.target.value }))}
              placeholder="受信者アドレス"
              disabled={loading}
            />
          </div>

          <div className="mt-4 space-y-2">
            <button
              type="button"
              className="flex items-center gap-2 text-sm font-medium text-blue-600"
              onClick={() => setShowCcBcc((prev) => !prev)}
              disabled={loading}
            >
              <span className={`transition-transform ${showCcBcc ? "rotate-90" : "rotate-0"}`}>▶</span>
              CC / BCC
            </button>
            {showCcBcc && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="compose-cc">
                    CC
                  </label>
                  <input
                    id="compose-cc"
                    type="text"
                    className="w-full rounded-lg border px-3 py-2"
                    value={composeForm.cc}
                    onChange={(e) => setComposeForm((prev) => ({ ...prev, cc: e.target.value }))}
                    placeholder="CC を入力"
                    disabled={loading}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="compose-bcc">
                    BCC
                  </label>
                  <input
                    id="compose-bcc"
                    type="text"
                    className="w-full rounded-lg border px-3 py-2"
                    value={composeForm.bcc}
                    onChange={(e) => setComposeForm((prev) => ({ ...prev, bcc: e.target.value }))}
                    placeholder="BCC を入力"
                    disabled={loading}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="compose-subject">
              件名
            </label>
            <input
              id="compose-subject"
              type="text"
              className="w-full rounded-lg border px-3 py-2"
              value={composeForm.subject}
              onChange={(e) => setComposeForm((prev) => ({ ...prev, subject: e.target.value }))}
              placeholder="件名を入力"
              disabled={loading}
            />
          </div>

          <div className="mt-4 space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="compose-body">
              本文
            </label>
            <textarea
              id="compose-body"
              className="w-full min-h-[220px] rounded-lg border px-3 py-2 resize"
              value={composeForm.body}
              onChange={(e) => setComposeForm((prev) => ({ ...prev, body: e.target.value }))}
              placeholder="本文を入力してください"
              disabled={loading}
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="compose-attach">
                添付
              </label>
              <input
                id="compose-attach"
                type="text"
                className="w-full rounded-lg border px-3 py-2"
                value={composeForm.attach}
                onChange={(e) => setComposeForm((prev) => ({ ...prev, attach: e.target.value }))}
                placeholder="添付の情報"
                disabled={loading}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="compose-sign">
                署名
              </label>
              <input
                id="compose-sign"
                type="text"
                className="w-full rounded-lg border px-3 py-2"
                value={composeForm.sign}
                onChange={(e) => setComposeForm((prev) => ({ ...prev, sign: e.target.value }))}
                placeholder="署名を入力"
                disabled={loading}
              />
            </div>
          </div>

          {sendError && <div className="mt-4 text-sm text-red-600">{sendError}</div>}

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              className="rounded border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
              onClick={() => navigate("/admin/mail")}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              className="rounded bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 disabled:opacity-60"
              onClick={handleSend}
              disabled={sending || loading}
            >
              {sending ? "送信中..." : "Send Email"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
